/**
 * Subscribes to the WebSocket gateway and pumps `notification:new` events
 * into `useNotificationStore`. Mounted exactly once at the app shell.
 *
 * Behavior:
 *  - Connects only when the user has a tenant-scoped profile. Platform-only
 *    profiles (no tenant.id) skip the connect — the gateway rejects them
 *    anyway because the JWT lacks a valid tenant_id.
 *  - On the FIRST reconnect after a disconnect (not the initial connect),
 *    triggers a REST refresh of the unread count to cover messages missed
 *    while offline.
 *  - High-priority notifications (by type) raise a toast. Low-priority types
 *    update the bell silently.
 *  - On unmount or logout, disconnects and clears all listeners.
 */

import { useEffect, useRef } from 'react';
import { useToast } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useCartStore } from '@/stores/use-cart-store';
import {
  connectSocket,
  disconnectSocket,
  onSocketEvent,
  subscribeSocketStatus,
  type SocketStatus,
} from '@/lib/socket-client';
import type {
  Notification,
  NotificationType,
} from '@/services/notification.service';
import { RETURN_UPDATE_EVENT, type ReturnUpdateDetail } from '@/lib/return-status';

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * Sparse payload that the gateway emits. Mirror of the persisted row but
 * with `priority` absent (the DB has no such column today). Some fields
 * may be undefined if the gateway is rolled back to its old shape.
 */
interface IncomingNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  created_at: string;
  user_id?: string | null;
  location_id?: string | null;
  is_read?: boolean;
  data?: Record<string, unknown> | null;
}

// ─── Toast routing ──────────────────────────────────────────────────────────

/**
 * Notification types that warrant an in-app toast. Lower-priority types
 * (REPORT_READY, BACKUP_COMPLETE, etc.) just update the bell silently.
 *
 * Source of truth: `NotificationType` in services/notification.service.ts.
 * Heuristic: anything that demands shop-floor action gets a toast.
 */
const TOAST_WORTHY: ReadonlySet<string> = new Set<NotificationType>([
  'SYNC_CONFLICT',
  'LOW_STOCK',
  'CREDIT_LIMIT',
  'EXPENSE_APPROVAL',
  'APPROVAL_RESULT',
  // T11 — a customer answered a quote on the public link.
  'QUOTE_APPROVED',
  'QUOTE_REJECTED',
  'QUOTE_CHANGES_REQUESTED',
]);

const DANGER_TYPES: ReadonlySet<string> = new Set<NotificationType>([
  'SYNC_CONFLICT',
  'CREDIT_LIMIT',
  'QUOTE_REJECTED',
]);

const WARNING_TYPES: ReadonlySet<string> = new Set<NotificationType>([
  'LOW_STOCK',
  'EXPENSE_APPROVAL',
]);

function toastVariant(type: string, metadata?: Record<string, unknown>): 'success' | 'warning' | 'error' | 'info' {
  if (DANGER_TYPES.has(type)) return 'error';
  if (WARNING_TYPES.has(type)) return 'warning';
  if (type === 'QUOTE_APPROVED') return 'success';
  if (type === 'APPROVAL_RESULT') {
    // T03: 'failed' — an approved return that could not be processed.
    return metadata?.result === 'rejected' || metadata?.result === 'failed' ? 'error' : 'success';
  }
  return 'info';
}

// ─── Payload normalization ──────────────────────────────────────────────────

/**
 * Map the gateway's sparse payload onto the full POS Notification shape so
 * the store stays internally consistent. `priority` is hard-coded to MEDIUM
 * because the DB has no priority column today (see notifications.repository).
 */
function normalize(payload: IncomingNotification, fallbackUserId: string): Notification {
  return {
    id: payload.id,
    tenant_id: '',
    user_id: payload.user_id ?? fallbackUserId,
    type: payload.type as NotificationType,
    priority: 'MEDIUM',
    title: payload.title,
    message: payload.message,
    is_read: payload.is_read ?? false,
    action_url: null,
    metadata: payload.data ?? {},
    created_at: payload.created_at,
  };
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useRealtimeNotifications(): void {
  const profile = useAuthStore((s) => s.profile);
  const { toast } = useToast();

  // Refs avoid re-binding listeners every render and let us implement
  // "fire on first reconnect, not initial connect".
  const hasConnectedOnceRef = useRef(false);
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    // Need a tenant-scoped profile to connect — the gateway requires a
    // valid UUID tenant_id in the JWT and will disconnect otherwise.
    const tenantId = profile?.tenant?.id;
    const userId = profile?.id;
    if (!tenantId || !userId) return;

    connectSocket();

    // Refresh unread count on the FIRST reconnect after a disconnect, not
    // on the very first connect (the page already fetches on mount).
    const unsubscribeStatus = subscribeSocketStatus((next: SocketStatus) => {
      if (next === 'connected') {
        if (hasConnectedOnceRef.current) {
          // Reconnect after a drop — rehydrate to cover anything missed.
          useNotificationStore.getState().fetchUnreadCount();
        }
        hasConnectedOnceRef.current = true;
      }
    });

    const unsubscribeNew = onSocketEvent<IncomingNotification>('notification:new', (payload) => {
      // Defensive: drop malformed events rather than crashing the listener.
      if (!payload || typeof payload.id !== 'string' || !payload.type) return;

      const notification = normalize(payload, userId);
      const before = useNotificationStore.getState().notifications.length;
      useNotificationStore.getState().addNotification(notification);
      const after = useNotificationStore.getState().notifications.length;

      // FR-BILL-050: dispatch discount approval results to the cart store
      // so the pending state is resolved in real time.
      if (
        notification.type === 'APPROVAL_RESULT' &&
        notification.metadata?.entity_type === 'discount_override'
      ) {
        const result = notification.metadata.result as string;
        if (result === 'approved') {
          const type = notification.metadata.discount_type as 'PERCENTAGE' | 'FLAT';
          const value = Number(notification.metadata.discount_value);
          useCartStore.getState().resolvePendingDiscountApproval('approved', {
            type,
            // Server value is basis points for PERCENTAGE; the cart keeps whole percent.
            value: type === 'PERCENTAGE' ? value / 100 : value,
            reason: (notification.metadata.reason as string) ?? '',
          });
        } else {
          useCartStore.getState().resolvePendingDiscountApproval('rejected');
        }
      }

      // T03: a return approved / rejected / failed — open pages refetch it.
      if (
        notification.type === 'APPROVAL_RESULT' &&
        notification.metadata?.entity_type === 'return_approval'
      ) {
        window.dispatchEvent(
          new CustomEvent<ReturnUpdateDetail>(RETURN_UPDATE_EVENT, {
            detail: notification.metadata as ReturnUpdateDetail,
          }),
        );
      }

      // Store dedupes by id — only toast when the notification was actually
      // added (avoids double-toasting after a REST refresh + live push).
      if (after === before) return;
      if (!TOAST_WORTHY.has(notification.type)) return;

      toastRef.current({
        variant: toastVariant(notification.type, notification.metadata),
        title: notification.title,
        description: notification.message,
      });
    });

    return () => {
      unsubscribeNew();
      unsubscribeStatus();
      disconnectSocket();
      hasConnectedOnceRef.current = false;
    };
  }, [profile?.tenant?.id, profile?.id]);
}
